import {
	EcommerceProduct,
	EcommerceCartPayloadItem,
	GoogleTagManagerEventPayloadMap,
} from '@zyro-inc/site-modules/types';

export const googleTagManagerEvent = <K extends keyof GoogleTagManagerEventPayloadMap>(
	eventName: K,
	payload?: GoogleTagManagerEventPayloadMap[K],
): void => window?.gtag?.('event', eventName, payload);

export const getAddToCartEventPayload = (product: EcommerceProduct) => {
	const {
		currency,
		amount,
	} = product.variants[0].prices[0];

	const {
		id,
		title,
		seo_settings: seoSettings,
	} = product;

	const variant = product.variants[0];

	return {
		value: amount / 100,
		currency: currency.code.toUpperCase(),
		items: [
			{
				item_id: String(id),
				sku: variant.sku,
				item_name: title,
				title,
				slug: seoSettings.slug,
				price: amount / 100,
				quantity: 1,
			},
		],
	};
};

export const getBeginCheckoutEventPayload = (products: EcommerceProduct[]) => {
	const finalAmount = products.reduce((total, product) => {
		const { amount } = product.variants[0].prices[0];

		return total + amount / 100;
	}, 0);

	const items = products.map((product) => {
		const variant = product.variants[0];
		const { amount } = variant.prices[0];

		return {
			item_id: String(product.id),
			sku: variant.sku,
			item_name: product.title,
			title: product.title,
			slug: product.seo_settings.slug,
			price: amount / 100,
			quantity: 1,
		};
	});

	const { currency } = products[0].variants[0].prices[0];

	return {
		value: finalAmount,
		currency: currency.code.toUpperCase(),
		items,
	};
};

export const getPurchaseEventPayload = (products: EcommerceProduct[]) => {
	const finalAmount = products.reduce((total, product) => {
		const { amount } = product.variants[0].prices[0];

		return total + amount / 100;
	}, 0);

	const items = products.map((product) => {
		const variant = product.variants[0];
		const { amount } = variant.prices[0];

		return {
			item_id: String(product.id),
			sku: variant.sku,
			item_name: product.title,
			title: product.title,
			slug: product.seo_settings.slug,
			price: amount / 100,
			quantity: 1,
		};
	});

	const { currency } = products[0].variants[0].prices[0];

	return {
		value: finalAmount,
		currency: currency.code.toUpperCase(),
		items,
	};
};

/**
 * Enhanced checkout payload function that handles real cart quantities
 * @param products - Array of products in cart
 * @param quantifiedItems - Array of quantified cart items with real quantities
 */
export const getBeginCheckoutEventPayloadWithQuantities = (
	products: EcommerceProduct[],
	quantifiedItems: EcommerceCartPayloadItem[],
) => {
	const finalAmount = quantifiedItems.reduce((total, cartItem) => {
		const product = products.find((p) => p.variants.some((v) => v.id === cartItem.variant_id));

		if (!product) {
			return total;
		}

		const variant = product.variants.find((v) => v.id === cartItem.variant_id);

		if (!variant) {
			return total;
		}

		const { amount } = variant.prices[0];

		return total + (amount / 100) * cartItem.quantity;
	}, 0);

	const items = quantifiedItems.map((cartItem) => {
		const product = products.find((p) => p.variants.some((v) => v.id === cartItem.variant_id));

		if (!product) {
			return null;
		}

		const variant = product.variants.find((v) => v.id === cartItem.variant_id);

		if (!variant) {
			return null;
		}

		const { amount } = variant.prices[0];

		return {
			item_id: String(product.id),
			sku: variant.sku,
			item_name: product.title,
			title: product.title,
			slug: product.seo_settings.slug,
			price: amount / 100,
			quantity: cartItem.quantity,
		};
	}).filter(Boolean);

	const { currency } = products[0]?.variants[0]?.prices[0] || {
		currency: {
			code: 'USD',
		},
	};

	return {
		value: finalAmount,
		currency: currency.code.toUpperCase(),
		items,
	};
};
